import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, Mail, MessageSquare, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const EVENT_LABELS = {
  subscription_created: 'Subscription Dibuat',
  subscription_renewed: 'Subscription Diperpanjang',
  subscription_expiring: 'Subscription Akan Berakhir',
  subscription_expired: 'Subscription Berakhir',
  payment_reminder: 'Reminder Pembayaran',
  payment_failed: 'Pembayaran Gagal',
  limit_warning: 'Peringatan Limit',
  // Affiliate events — dikirim ke affiliator (Tokomoe Affiliate Program)
  affiliate_approved: '🤝 Affiliate: Akun Disetujui',
  affiliate_new_referral: '🤝 Affiliate: Referral Baru',
  affiliate_commission_earned: '🤝 Affiliate: Komisi Masuk',
  affiliate_withdrawal_requested: '🤝 Affiliate: Withdrawal Diajukan',
  affiliate_withdrawal_processed: '🤝 Affiliate: Withdrawal Diproses',
};

// Variabel yang tersedia per event (untuk hint di editor).
const EVENT_VARIABLES = {
  affiliate_approved: ['{name}', '{code}', '{commission_rate}', '{referral_link}'],
  affiliate_new_referral: ['{name}', '{tenant_name}', '{code}', '{created_at}'],
  affiliate_commission_earned: ['{name}', '{tenant_name}', '{commission_type}', '{amount}', '{pending_commission}'],
  affiliate_withdrawal_requested: ['{name}', '{amount}', '{bank_name}', '{account_number}', '{account_name}'],
  affiliate_withdrawal_processed: ['{name}', '{amount}', '{bank_name}', '{account_number}', '{status_label}', '{note}', '{message}'],
};

export default function AdminNotificationTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ subject: '', message: '', is_active: true });
  const [saving, setSaving] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/notification-templates');
      setTemplates(res.data.templates || []);
    } catch { toast.error('Gagal memuat template'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const openEdit = (t) => {
    setEditTarget(t);
    setForm({ subject: t.subject || '', message: t.message || '', is_active: t.is_active !== false });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/notification-templates/${editTarget.id}`, form);
      toast.success('Template disimpan');
      setEditTarget(null);
      fetchTemplates();
    } catch { toast.error('Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/admin/notification-templates/${t.id}`, { is_active: !t.is_active });
      fetchTemplates();
    } catch { toast.error('Gagal mengubah status'); }
  };

  // Group by event
  const groupedByEvent = templates.reduce((acc, t) => {
    if (!acc[t.event]) acc[t.event] = [];
    acc[t.event].push(t);
    return acc;
  }, {});

  const emailTemplates = templates.filter(t => t.channel === 'email');
  const waTemplates = templates.filter(t => t.channel === 'whatsapp');

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Notification Templates</h1>
        <p className="text-sm text-slate-500 mt-1">Template notifikasi platform yang dikirim ke tenant</p>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>
      ) : (
        <Tabs defaultValue="by-event">
          <TabsList className="mb-6">
            <TabsTrigger value="by-event"><Bell size={14} className="mr-1.5" />Per Event</TabsTrigger>
            <TabsTrigger value="email"><Mail size={14} className="mr-1.5" />Email ({emailTemplates.length})</TabsTrigger>
            <TabsTrigger value="whatsapp"><MessageSquare size={14} className="mr-1.5" />WhatsApp ({waTemplates.length})</TabsTrigger>
          </TabsList>

          {/* By Event */}
          <TabsContent value="by-event">
            <div className="space-y-4">
              {Object.entries(groupedByEvent).map(([event, tpls]) => (
                <Card key={event} className="border-slate-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold text-slate-700">
                      {EVENT_LABELS[event] || event}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-100">
                      {tpls.map(t => (
                        <div key={t.id} className="flex items-start gap-4 px-6 py-4">
                          <div className={`mt-0.5 p-1.5 rounded-lg ${t.channel === 'email' ? 'bg-blue-50' : 'bg-green-50'}`}>
                            {t.channel === 'email' ? (
                              <Mail size={15} className="text-blue-600" />
                            ) : (
                              <MessageSquare size={15} className="text-green-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs capitalize">{t.channel}</Badge>
                              {t.subject && <span className="text-sm font-medium text-slate-900">{t.subject}</span>}
                            </div>
                            <p className="text-sm text-slate-500 line-clamp-2 whitespace-pre-line">{t.message}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              Variabel: {'{tenant_name}'}, {'{plan_name}'}, {'{expiry_date}'}, {'{days}'}, {'{amount}'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Switch checked={t.is_active} onCheckedChange={() => toggleActive(t)} />
                            <Button variant="ghost" size="icon" onClick={() => openEdit(t)}>
                              <Pencil size={15} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email">
            <TemplateList templates={emailTemplates} onEdit={openEdit} onToggle={toggleActive} />
          </TabsContent>

          {/* WhatsApp Tab */}
          <TabsContent value="whatsapp">
            <TemplateList templates={waTemplates} onEdit={openEdit} onToggle={toggleActive} />
          </TabsContent>
        </Tabs>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Template Notifikasi</DialogTitle>
            {editTarget && (
              <p className="text-sm text-slate-500">{EVENT_LABELS[editTarget.event] || editTarget.event} — {editTarget.channel}</p>
            )}
          </DialogHeader>
          <div className="space-y-4 py-2">
            {editTarget?.channel === 'email' && (
              <div className="space-y-2">
                <Label>Subject Email</Label>
                <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Pesan</Label>
              <Textarea
                value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                rows={8}
                className="font-mono text-sm"
              />
              {EVENT_VARIABLES[editTarget?.event] ? (
                <div className="text-xs text-slate-500">
                  <span className="font-medium">Variabel tersedia untuk event ini:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {EVENT_VARIABLES[editTarget.event].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setForm(p => ({ ...p, message: (p.message || '') + ' ' + v }))}
                        className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-[#2D6CDF]/10 font-mono text-[11px] text-[#2D6CDF]"
                        title="Klik untuk menyisipkan"
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-400">
                  Variabel tersedia: {'{tenant_name}'}, {'{plan_name}'}, {'{expiry_date}'}, {'{days}'}, {'{amount}'}, {'{feature}'}, {'{current}'}, {'{max}'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} />
              <Label>Template Aktif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Batal</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
              {saving ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateList({ templates, onEdit, onToggle }) {
  if (!templates.length) return (
    <div className="py-12 text-center text-slate-400">Tidak ada template</div>
  );
  const EVENT_LABELS = {
    subscription_created: 'Subscription Dibuat',
    subscription_renewed: 'Subscription Diperpanjang',
    subscription_expiring: 'Subscription Akan Berakhir',
    subscription_expired: 'Subscription Berakhir',
    payment_reminder: 'Reminder Pembayaran',
    payment_failed: 'Pembayaran Gagal',
    limit_warning: 'Peringatan Limit',
  };
  return (
    <div className="space-y-3">
      {templates.map(t => (
        <Card key={t.id} className={`border-slate-200 shadow-sm ${!t.is_active ? 'opacity-60' : ''}`}>
          <CardContent className="p-4">
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">{EVENT_LABELS[t.event] || t.event}</Badge>
                  {!t.is_active && <Badge className="bg-slate-100 text-slate-500 text-xs">Nonaktif</Badge>}
                </div>
                {t.subject && <p className="font-medium text-sm text-slate-900 mb-1">{t.subject}</p>}
                <p className="text-sm text-slate-500 line-clamp-3 whitespace-pre-line">{t.message}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={t.is_active} onCheckedChange={() => onToggle(t)} />
                <Button variant="ghost" size="icon" onClick={() => onEdit(t)}>
                  <Pencil size={15} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
